import React, { lazy, Suspense, ReactElement, ComponentType } from "react";
import { CheckoutRoute } from "@swiggy-private/checkout-core";
import { QuickCheckoutCartShimmer, RouteProps } from "@swiggy-private/core-ui";
import CartUIWrapper from "@cart-client/components/cart-renderer/cart-ui-wrapper";
import { Shimmer } from "@cart-client/components/shimmer";

type CartRouteProps = RouteProps<CheckoutRoute>;

const createLazyCartRoute = (
    load: () => Promise<{ default: ComponentType<CartRouteProps> }>,
    fallback: ReactElement,
): ((props: CartRouteProps) => ReactElement) => {
    const LazyRouteComponent = lazy(load);
    // eslint-disable-next-line react/display-name
    return (props: CartRouteProps): ReactElement => (
        <Suspense fallback={fallback}>
            <LazyRouteComponent {...props} />
        </Suspense>
    );
};

/** Same shell MROFoodCartRenderer uses while cart data loads. `null` painted a white frame. */
const MROCartChunkFallback = (
    <CartUIWrapper>
        <Shimmer />
    </CartUIWrapper>
);

/** Compact AIP sheet shimmer. Do not reuse MRO/full-cart `Shimmer` — QC webview is ~30% height. */
const QuickCheckoutCartChunkFallback = <QuickCheckoutCartShimmer />;

export const MROFoodCartRoute = createLazyCartRoute(
    () =>
        import(
            /* webpackChunkName: "mro_and_aip" */
            "@cart-client/components/mro-cart/mro-food-cart-renderer/MROFoodCartRenderer"
        ),
    MROCartChunkFallback,
);

export const MROCouponListingRoute = createLazyCartRoute(
    () =>
        import(
            /* webpackChunkName: "mro_and_aip" */
            "@cart-client/components/mro-cart/mro-coupon-listing/MROCouponListing"
        ),
    MROCartChunkFallback,
);

export const QuickCheckoutCartRoute = createLazyCartRoute(
    () =>
        import(
            /* webpackChunkName: "mro_and_aip" */
            "@cart-client/components/quick-checkout-cart/quick-checkout-cart-renderer/QuickCheckoutCartRenderer"
        ),
    QuickCheckoutCartChunkFallback,
);
